import { Box, colors, IconButton, styled, Theme } from "@mui/material";
import React from "react";
import Localization from "../core/Localization";
import FocusRipple from "../coreui/FocusRipple";
import Icon from "../coreui/Icon";
import KeyboardNavigationGroup from "../coreui/KeyboardNavigationGroup";
import { GroupedNavigationContext } from "../coreui/GroupedNavigationContext";
import Paper from "../coreui/Paper";
import Presentation from "../coreui/Presentation";
import { RowErrorBadge } from "../coreui/RowErrorBadge";
import { TableChildProps, TableVerticalLayoutProps } from "../coreui/Table";
import { NoPrint } from "../coreui/table/NoPrint";
import PaneRow from "../models/PaneRow";
import { GridNewRowChip } from "./GridNewRowChip";
import { Toolbar as ToolbarBase } from "./Toolbar";

const TopContainer = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
});

const NewContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  marginBottom: "24px",
});

const NewContainerBorder = styled("div")({
  borderBottom: `2px solid ${colors.lightGreen[500]}`,
  position: "relative",
  top: "12px",
});

export const Card = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLDivElement> & {
    cardIndex: number;
    cardDepth: number;
    errors?: string[];
    id: string;
    paneConfig: object;
    row: PaneRow;
    propagated: TableChildProps;
    selectionToolbarConfig?: object;
  }
>((props, ref) => {
  const {
    cardIndex,
    cardDepth,
    errors,
    paneConfig,
    row,
    propagated,
    selectionToolbarConfig,
    ...rootProps
  } = props;

  const { enterGroup } = React.useContext(GroupedNavigationContext);
  const [rootFocused, setRootFocused] = React.useState(false);

  const enterRowDescriptionId = `${props.id}-enterrow-description`;
  const summaryToolbarDescriptionId = `${props.id}-summary-toolbar-description`;

  return (
    <Paper
      aria-invalid={!!errors}
      aria-label={row.objectTitleIsDefault ? undefined : row.objectTitle}
      card
      cardDepth={cardDepth}
      data-enterrow
      elevation={0}
      margin
      onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
        if (event.currentTarget === event.target) {
          setRootFocused(false);
        }
      }}
      onFocus={(event: React.FocusEvent<HTMLDivElement>) => {
        if (event.currentTarget === event.target) {
          setRootFocused(true);
        }
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (
          (event.key === "Enter" || event.key === " ") &&
          document.activeElement === event.currentTarget
        ) {
          enterGroup();
          event.stopPropagation();
          event.preventDefault();
        }
      }}
      ref={ref}
      sx={[
        {
          "&:focus": {
            outline: "none",
          },
          whiteSpace: "normal",
        },
        (theme: Theme) =>
          errors && {
            border: `2px solid ${theme.palette.danger.main}`,
            padding: `${theme.paper.padding.xs - 2}px !important`,
          },
      ]}
      tabIndex={-1}
      {...rootProps}
    >
      {row.isNew && (
        <React.Fragment>
          <NewContainerBorder />
          <NewContainer>
            <GridNewRowChip inVerticalLayout />
          </NewContainer>
        </React.Fragment>
      )}
      <NoPrint>
        <TopContainer>
          <IconButton
            aria-describedby={enterRowDescriptionId}
            aria-label={Localization.getBuiltInMessage("DataTable.enterRow")}
            data-skip-focus
            onClick={() => enterGroup()}
            size="small"
            sx={{ color: "inherit" }}
            tabIndex={-1}
          >
            <div id={enterRowDescriptionId} style={{ display: "none" }}>
              {row.objectTitleIsDefault ? null : row.objectTitle}
            </div>
            <FocusRipple visible={rootFocused} />
            <Icon icon="far fa-square-arrow-down-right" />
          </IconButton>
          {errors && (
            <RowErrorBadge
              errors={errors}
              propagated={propagated}
              rowKey={row.rowKey}
            />
          )}
        </TopContainer>
      </NoPrint>
      {Presentation.create(paneConfig, {
        parentTable: propagated.parentTable,
        rowKey: row.rowKey,
      } as TableVerticalLayoutProps)}
      <NoPrint>
        {selectionToolbarConfig ? (
          <KeyboardNavigationGroup
            aria-describedby={summaryToolbarDescriptionId}
            childSelector={ToolbarBase.childSelector}
            role="toolbar"
          >
            <div id={summaryToolbarDescriptionId} style={{ display: "none" }}>
              {row.objectTitleIsDefault
                ? Localization.getBuiltInMessage(
                    "DataTable.selectionToolbarCardLabelNoTitle"
                  )
                : Localization.getBuiltInMessage(
                    "DataTable.selectionToolbarCardLabel",
                    {
                      title: row.objectTitle,
                    }
                  )}
            </div>
            {Presentation.create(selectionToolbarConfig, {
              ...propagated,
              data: row,
              parentRowKey: row.rowKey,
              parentRowObjectHandle: row.objectHandle,
            })}
          </KeyboardNavigationGroup>
        ) : null}
      </NoPrint>
    </Paper>
  );
});
